// @ts-nocheck
import { navigate } from 'svelte-routing';
import { clearUser, getAuthToken, getRefreshToken } from '../stores/auth';
import { loadingData } from '../stores/loadingData';
import * as authController from './controllers/auth';
import * as profileController from './controllers/profile';
import * as masterController from './controllers/master';
import * as clientTypeController from './controllers/clientType';
import * as registrationTypeController from './controllers/registrationType';
import * as projectTypeController from './controllers/projectType';
import * as serviceProviderTypeController from './controllers/serviceProviderType';
import * as fundStrategyController from './controllers/fundStrategy';
import * as sowTypeController from './controllers/sowType';
import * as codeEthicsController from './controllers/codeEthics';
import * as internalProjectController from './controllers/internalProject';
import * as clientStatusController from './controllers/clientStatus';
import * as userController from './controllers/user';
import * as clientController from './controllers/client';
import * as taskController from './controllers/task';
import * as taskTemplateController from './controllers/taskTemplate';
import * as notificationController from './controllers/notification';
import * as activityController from './controllers/activity';
import * as activityTypeController from './controllers/activityType';

import { configItem } from '../components/Helper.js';
const BASE_URL = configItem.baseUrl;

// Endpoints to which we shouldn't send authorization header
const ENDPOINTS_AUTH_TOKEN_BLACKLIST = ['/login', '/forgot-password'].map(
  (url) => new URL(BASE_URL + url).href,
);

const getCommonHeaders = (relativeUrl, skipContentType = false) => {
  const commonHeaders = {};

  // Only add content-type for JSON requests
  if (!skipContentType) {
    commonHeaders['content-type'] = 'application/json';
  }

  // Do not append authorization header to excluded endpoints
  if (!ENDPOINTS_AUTH_TOKEN_BLACKLIST.includes(relativeUrl)) {
    commonHeaders.authorization = `Bearer ${getAuthToken()}`;
  }

  return commonHeaders;
};

function resolveUrl(relativeUrl, queries = {}) {
  const url = new URL(BASE_URL + relativeUrl);
  Object.keys(queries).forEach((q) => {
    if (queries[q] !== null) {
      url.searchParams.set(q, queries[q]);
    }
  });

  return url.href;
}

function resolveAsJson(response) {
  return response.json();
}

const sleep = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

let firstRefresh = false;

// Function to refresh the token
export const fetchNewToken = async () => {
  if (firstRefresh) return false;
  firstRefresh = true;
  try {
    const resp = await fetch(`${BASE_URL}/v2/token/refresh`, {
      method: 'POST',
      headers: {
        Authorization: 'Bearer ' + getRefreshToken(),
      },
    });
    const respJson = await resp.json();
    if (respJson?.data?.access_token && respJson?.data?.refresh_token) {
      localStorage.setItem('token', respJson.data.access_token);
      localStorage.setItem('refreshToken', respJson.data.refresh_token);
      await sleep(1000);
      firstRefresh = false;
      return true;
    } else {
      clearUser();
    }
  } catch {
    clearUser();
  }
  return false;
};

async function fetchResource(url, options) {
  const headers = new Headers(options.headers);
  const skipContentType = options.body instanceof FormData;
  const commonHeaders = getCommonHeaders(url, skipContentType);
  Object.keys(commonHeaders).forEach((header) => {
    headers.set(header, commonHeaders[header]);
  });
  let showLoader = url.indexOf('get-unread-count') !== -1 ? false : true;

  if (showLoader) {
    loadingData.set(true);
  }
  try {
    const response = await window.fetch(url, { ...options, headers });
    if (showLoader) {
      loadingData.set(false);
    }
    if (response.status == 403) {
      window.location = '/invalid-access';
      return Promise.reject();
    }
    // if (!response.ok) {
    //   throw Error("Server error");
    // }

    //   Parse JSON response automatically when api response has the explicit header
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const parsedResponse = await resolveAsJson(response);
      // API return Invalid token message
      if (parsedResponse.message === 'Invalid token') {
        fetchNewToken();
        return Promise.reject();
      }
      // API returns 200 as HTTP code all the time
      if (parsedResponse.status?.code == 200) {
        return parsedResponse;
      } else if (parsedResponse.status?.code == 401) {
        fetchNewToken();
        return Promise.reject();
      }
      // Handle global unauthenticated response
      else {
        if (parsedResponse.errorType != undefined && parsedResponse.errorType == 'INVALID_ACCESS') {
          window.location = '/invalid-access';
          return Promise.reject();
        }
        throw new APIError(
          parsedResponse.status.message || parsedResponse.message || 'Unkown Error',
        );
      }
    }

    return response;
  } catch (err) {
    if (showLoader) {
      loadingData.set(false);
    }
    throw new APIError(err || 'Unkown Error');
  }
}

export const _baseClient = {
  get(url, queries = {}, options = {}) {
    return fetchResource(resolveUrl(url, queries), {
      ...options,
      method: 'GET',
    });
  },
  post(url, options = {}) {
    return fetchResource(resolveUrl(url), {
      ...options,
      method: 'POST',
      body: JSON.stringify(options.body),
    });
  },
  postFormData(url, options = {}) {
    return fetchResource(resolveUrl(url), {
      ...options,
      method: 'POST',
      body: options.body, // FormData - don't stringify
      headers: {
        // Don't set content-type, let browser set it with boundary for multipart
        ...options.headers,
      },
    });
  },
};

export class APIError extends Error {
  constructor(message) {
    super(message);
  }

  toString() {
    return this.message.replace('Error: ', '').trim();
  }
}

export const client = {
  ...authController,
  ...profileController,
  ...masterController,
  ...clientTypeController,
  ...registrationTypeController,
  ...projectTypeController,
  ...serviceProviderTypeController,
  ...sowTypeController,
  ...fundStrategyController,
  ...codeEthicsController,
  ...internalProjectController,
  ...internalProjectController,
  ...userController,
  ...clientController,
  ...taskController,
  ...taskTemplateController,
  ...notificationController,
  ...activityController,
  ...clientStatusController,
  ...activityTypeController,
};
